import { Injectable } from '@angular/core';
import { WindowReferenceService } from './window-reference.service';
import { MascotaDataLayer, PetDataLayerItem } from '../../../domain/interfaces/tagging/datalayer.interface';
/**
 * Mapa campo de MascotaDataLayer -> sufijo usado en el dataLayer
 * (pet_mascotaN_<sufijo>). Agregar/quitar un campo del tracking de
 * mascotas solo requiere tocar este mapa y la interfaz, no la lógica
 * de armado del hit.
 */
const MASCOTA_FIELD_SUFFIX: Record<keyof MascotaDataLayer, string> = {
  nombre: 'nombre',
  tipo: 'tipo',
  sexo: 'sexo',
  raza: 'raza',
  edad: 'edad',
  ciudad: 'ciudad',
  agresion: 'agresion',
  razaPeligrosa: 'raza_peligrosa',
  vacunacionCompleta: 'vacunacion_completa',
  enfermedadMesPrevio: 'enfermedad_mes_previo',
  cirugiaProgramada: 'cirugia_programada',
  enfermedadCronica: 'enfermedad_cronica',
  usoSeguridadCriadero: 'uso_seguridad_criadero',
  planSeleccionado: 'plan_seleccionado',
  planPeriodicidad: 'plan_periodicidad',
  coberturaRc: 'cobertura_rc'
};

@Injectable({
  providedIn: 'root'
})
export class PetDataLayerService {

  private window;

  constructor(private _windowRef: WindowReferenceService) {
    this.window = _windowRef.nativeWindow; // intialise the window to what we get from our window service
  }

  private pingHome(hit: Record<string, unknown>) {
    if (hit) this.window.dataLayer.push(hit);
  }

  /**
   * Convierte el arreglo de mascotas en las llaves planas que espera
   * el dataLayer: pet_mascota1_nombre, pet_mascota1_tipo, ...,
   * pet_mascota2_nombre, etc. El índice se asigna por posición en el
   * arreglo (1 a 5, según la guía de medición).
   */
  private flattenMascotas(mascotas: MascotaDataLayer[] = []): Record<string, string> {
    const flat: Record<string, string> = {};
    mascotas.forEach((mascota, index) => {
      const numero = index + 1;
      (Object.keys(MASCOTA_FIELD_SUFFIX) as (keyof MascotaDataLayer)[]).forEach((campo) => {
        const valor = mascota[campo];
        if (valor !== undefined && valor !== null) {
          flat[`pet_mascota${numero}_${MASCOTA_FIELD_SUFFIX[campo]}`] = valor;
        }
      });
    });
    return flat;
  }

  /**
   * Método genérico y reutilizable para todo el flujo de mascotas.
   * Arma el hit base (event, pet_item, pet_tipo, pet_pantalla, user_id),
   * aplana las mascotas recibidas y agrega cualquier dato adicional
   * propio de la pantalla (additionalData).
   *
   * Todas las pantallas del flujo de mascotas (pasos 1 al 18, según la
   * Guía de medición SURA) invocan este método a través de
   * LogPetEventUseCase, pasando el `step` y `pantalla` correspondientes.
   */
  logPetEvent(
    step: number,
    item: PetDataLayerItem,
    pantalla: string,
    userId: string,
    mascotas: MascotaDataLayer[] = [],
    additionalData: Record<string, string | number> = {}
  ) {
    const hit = {
      event: `pet_step_${step}`,
      pet_item: item,
      pet_tipo: 'Mascotas',
      pet_pantalla: pantalla,
      user_id: userId,
      ...this.flattenMascotas(mascotas),
      ...additionalData
    };

    this.pingHome(hit);
  }

  /**
   * Paso 15 - pageview/button - "Error 404"
   * Pendiente de integrar en el manejo global de errores HTTP / la página de error.
   */
  logError404(item: PetDataLayerItem, userId: string) {
    this.logPetEvent(15, item, 'Error 404', userId);
  }

  /**
   * Paso 16 - "Error 500"
   * Nota: según el Excel de la guía, la fila "button" de este paso registra
   * la pantalla como "Error 404" en vez de "Error 500". Se deja `pantalla`
   * como parámetro para ajustarlo fácilmente cuando el equipo de analítica
   * confirme el valor correcto. Pendiente de integrar en el manejo global
   * de errores HTTP / la página de error.
   */
  logError500(item: PetDataLayerItem, userId: string, pantalla = 'Error 500') {
    this.logPetEvent(16, item, pantalla, userId);
  }
}
